import React, { Component } from 'react';
import type { CSSProperties } from 'react';
import { observable, makeObservable, runInAction, action } from 'mobx';
import { observer } from 'mobx-react';

import type { Message, Model } from '../models';
import apiService from '@services/apiService';
import { helperService } from '@services/helperService';
import modelService from '@services/modelService';
import { CompletionType, PromiseCompletion } from '@classes/PromiseCompletion';
import { chatExecutionService } from '@services/chatExecutionService';
import { ImgCompact } from './Images/ImgCompact';

export type TokensCounterProps = {
  isGenerating: boolean;
  className?: string;
  currentModel: Model | null;
  hasImage: boolean;
  inputValue: string;
  tools?: string[] | null;
  conversation?: Message[];
  attachmentFiles?: File[];
  predictLength?: string | null;
  onTokensChange?: (tokens: number | null) => void;
};

type TokensBreakdown = {
  system: number;
  userMessages: number;
  assistantMessages: number;
  thinking: number;
  toolResults: number;
  attachments: number;
  internalTools: number;
  mcpTools: number;
  image: number;
};

const R = 6, CX = 8, CY = 8;
const CIRCUMFERENCE = 2 * Math.PI * R;

@observer
class TokensCounter extends Component<TokensCounterProps> {
  @observable private _convBreakdown: TokensBreakdown | null = null;
  @observable private _promptTokens: number = 0;
  @observable private _isOpen: boolean = false;
  @observable private _isHidingPopup: boolean = false;
  @observable private _isHovered: boolean = false;
  @observable private _calibrationFactor: number = 1;
  private _loader = new PromiseCompletion(CompletionType.Completed);
  private _popupHideTimeout: ReturnType<typeof setTimeout> | null = null;

  private _updateConversation: (() => void) & { cancel: () => void } | null = null;
  private _updatePrompt: (() => void) & { cancel: () => void } | null = null;
  private _convCacheKey: string = '';
  private _chatIdentityKey: string = '';

  constructor(props: TokensCounterProps) {
    super(props);
    makeObservable(this);
  }

  componentDidMount() {
    this._updateConversation = helperService.throttle(this._doUpdateConversation, 5000);
    this._updatePrompt = helperService.debounce(this._doUpdatePrompt, 800);
    this._chatIdentityKey = this._makeChatIdentityKey();
    this._updateConversation();
    this._updatePrompt();
    document.addEventListener('click', this._onDocumentClick);
  }

  componentDidUpdate(prevProps: Readonly<TokensCounterProps>) {
    const { inputValue, hasImage, tools, currentModel } = this.props;
    const convKey = this._makeConvKey();
    const newIdentity = this._makeChatIdentityKey();

    if (newIdentity !== this._chatIdentityKey) {
      // Chat switched — clear stale data immediately and bypass throttle
      this._chatIdentityKey = newIdentity;
      runInAction(() => {
        this._convBreakdown = null;
        this._promptTokens = 0;
        this._calibrationFactor = 1;
        this._convCacheKey = '';
      });
      this._doUpdateConversation();
      this._doUpdatePrompt();
      return;
    }

    if (convKey !== this._convCacheKey || prevProps.tools?.join(',') !== tools?.join(',') || prevProps.currentModel !== currentModel) {
      this._updateConversation?.();
    }
    if (prevProps.inputValue !== inputValue || prevProps.hasImage !== hasImage
        || prevProps.attachmentFiles !== this.props.attachmentFiles) {
      this._updatePrompt?.();
    }
  }

  componentWillUnmount() {
    this._updateConversation?.cancel();
    this._updatePrompt?.cancel();
    document.removeEventListener('click', this._onDocumentClick);
    if (this._popupHideTimeout !== null) {
      clearTimeout(this._popupHideTimeout);
      this._popupHideTimeout = null;
    }
  }

  private _makeConvKey(): string {
    const { conversation } = this.props;
    if (!conversation || conversation.length === 0) return '';
    const last = conversation[conversation.length - 1];
    return `${conversation.length}:${last?.id ?? last?.timestamp ?? ''}`;
  }

  private _makeChatIdentityKey(): string {
    const { conversation } = this.props;
    if (!conversation || conversation.length === 0) return '';
    // Use the first non-system message ID as the stable identity of a chat
    const first = conversation.find(m => m.role !== 'system') ?? conversation[0];
    return first?.id ?? String(first?.timestamp ?? '');
  }

  private _getCalibrationFactor(breakdown: TokensBreakdown): number {
    const { conversation } = this.props;
    if (!conversation) return 1;
    // After context compaction, only use assistant messages that come AFTER the last context
    // message for calibration. Pre-compaction assistant messages have tokensIn from the old
    // (uncompacted) context, which would inflate the calibration factor.
    const lastContextIdx = conversation.reduce((best, m, i) => m.role === 'context' ? i : best, -1);
    const slice = lastContextIdx >= 0 ? conversation.slice(lastContextIdx + 1) : conversation;
    const asst = [...slice].reverse().find(m => m.role === 'assistant' && (m.tokensIn ?? 0) > 0);
    if (!asst) return 1;
    const estTotal = Object.values(breakdown).reduce((a, b) => a + (b as number), 0);
    if (estTotal <= 0) return 1;
    return asst.tokensIn! / estTotal;
  }

  private get _total(): number | null {
    if (!this._convBreakdown && this._promptTokens === 0) return null;
    const convTotal = this._convBreakdown
      ? Object.values(this._convBreakdown).reduce((a, b) => a + (b as number), 0)
      : 0;
    return Math.round((convTotal + this._promptTokens) * this._calibrationFactor);
  }

  render() {
    const model = this.props.currentModel;
    if (!model) {
      return null;
    }
    // Priority: actual loaded context > admin-recommended context > model max context
    const activeLoaded = model
      ? modelService.activeModels.find(m => m.name === model.name && m.server === model.server)
      : null;
    let contextLength = activeLoaded?.context_length
      || model?.recommendedContextSize
      || model?.context_length
      || null;

    if (!contextLength || !this._total || !this._convBreakdown || !this._loader.isCompleted) {
      return null;
    }

    if (window.user?.role === 'guest') contextLength = Math.min(contextLength, 4096);

    const total = this._total;
    const fill = total !== null ? Math.min(total / contextLength, 1) : 0;
    const pct = fill * 100;
    const color = pct >= 85 ? 'var(--danger-color)' : pct >= 70 ? 'var(--warning-color, #e08a00)' : 'var(--success-color)';
    const textColor = pct >= 85 ? 'var(--danger-color)' : pct >= 70 ? 'var(--warning-color, #e08a00)' : 'var(--text-primary)';
    const shortTotal = total !== null
      ? (total >= 1000 ? `${(total / 1000).toFixed(total >= 10000 ? 0 : 1)}k` : String(total))
      : '~';
    const shortLimit = contextLength >= 1024
      ? `${Math.floor(contextLength / 1024)}k`
      : String(contextLength);

    const dashOffset = CIRCUMFERENCE * (1 - fill);

    const isLow = total === null || pct < 50;

    return (
      <div
        className={`tc-wrap ${this.props.className || ''} ${this._isHovered ? 'tc-hovered' : ''} ${this._isOpen ? 'tc-open' : ''} ${isLow && !this._isOpen ? 'tc-low' : ''}`}
        onMouseEnter={this._onMouseEnter}
        onMouseLeave={this._onMouseLeave}
        onClick={this._onClick}
        title={`Context: ~${shortTotal} / ${shortLimit} tokens`}
      >
        <svg className="tc-ring" viewBox="0 0 16 16" fill="none">
          <circle cx={CX} cy={CY} r={R} strokeWidth="2" className="tc-ring-bg" />
          <circle
            cx={CX} cy={CY} r={R} strokeWidth="2"
            stroke={color}
            strokeLinecap="round"
            strokeDasharray={CIRCUMFERENCE}
            strokeDashoffset={dashOffset}
            transform={`rotate(-90 ${CX} ${CY})`}
            className="tc-ring-fg"
          />
        </svg>
        <span className="tc-label">
          <span className="tc-total" style={{ color: textColor }}>{shortTotal}</span>
        </span>

        {(this._isOpen || this._isHidingPopup) && this._renderPopup(contextLength, color, textColor, pct)}
      </div>
    );
  }

  private _getResponseTokens(): number {
    const { predictLength, tools } = this.props;
    if (predictLength && predictLength !== 'auto') {
      const n = parseInt(predictLength, 10);
      if (!isNaN(n)) return n * 1024;
    }
    const modelName = this.props.currentModel?.name?.toLowerCase() ?? '';
    let tokens = 4096;
    if (modelName.includes('gpt-oss')) tokens = 8192;
    if (modelName.includes('code') || modelName.includes('coding')) tokens = 20480;
    if (tools && tools.length > 0) tokens = Math.min(tokens * 2, 20480);
    return tokens;
  }

  private _renderPopup(contextLength: number, accentColor: string, textColor: string, pct: number) {
    const { isGenerating } = this.props;
    const b = this._convBreakdown;
    const f = this._calibrationFactor;
    const cal = (v: number) => Math.round(v * f);
    const fmt = (v: number) => v >= 1000 ? `${(v / 1000).toFixed(v >= 10000 ? 0 : 1)}k` : String(v);
    const total = this._total;

    const rows: { label: string; value: number }[] = [
      { label: 'System prompt', value: cal(b?.system ?? 0) },
      { label: 'Tools: built-in', value: cal(b?.internalTools ?? 0) },
      { label: 'Tools: MCP', value: cal(b?.mcpTools ?? 0) },
      { label: 'Attachments', value: cal(b?.attachments ?? 0) },
      { label: 'Tool results', value: cal(b?.toolResults ?? 0) },
      { label: 'User messages', value: cal(b?.userMessages ?? 0) },
      { label: 'Assistant replies', value: cal(b?.assistantMessages ?? 0) },
      { label: 'Thinking', value: cal(b?.thinking ?? 0) },
      { label: 'Image', value: cal(b?.image ?? 0) },
      { label: 'Current prompt', value: Math.round(this._promptTokens * f) },
    ].filter(r => r.value > 0);

    const barColor = pct >= 85 ? 'var(--danger-color)' : pct >= 70 ? 'var(--warning-color, #e08a00)' : accentColor;
    const reservedColor = pct >= 70 ? 'var(--warning-color, #e08a00)' : 'var(--success-color, #139f13)';

    const responseTokens = this._getResponseTokens();
    const usedPct = Math.min(pct, 100);
    const reservedPct = Math.min(responseTokens / contextLength * 100, Math.max(0, 100 - usedPct));
    const freePct = Math.max(0, 100 - usedPct - reservedPct);

    return (
      <div className={`tc-popup${this._isHidingPopup ? ' tc-popup--hiding' : ''}`} onClick={e => e.stopPropagation()}>
        <div className="tc-popup-title">Context usage · {Math.round(pct)}%</div>
        {rows.length > 0 ? rows.map(r => (
          <div key={r.label} className="tc-popup-row">
            <span className="tc-popup-label">{r.label}</span>
            <span className="tc-popup-bar-wrap">
              <span
                className="tc-popup-bar"
                style={{ width: `${Math.min(r.value / contextLength * 100, 100)}%`, background: barColor }}
              />
            </span>
            <span className="tc-popup-value">{fmt(r.value)}</span>
          </div>
        )) : (
          <div className="tc-popup-empty">No context yet</div>
        )}
        {f !== 1 && (
          <div className="tc-popup-calibrated">&#10003; Calibrated from conversation history</div>
        )}
        <div className="tc-ctx-bar-track">
          <div className="tc-ctx-bar-used" style={{ width: `${usedPct}%`, background: barColor }} />
          {reservedPct > 0 && (
            <div
              className="tc-ctx-bar-reserved"
              style={{
                width: `${reservedPct}%`,
                '--tc-reserved-color': reservedColor,
              } as CSSProperties}
            />
          )}
          {freePct > 0 && <div className="tc-ctx-bar-free" style={{ width: `${freePct}%` }} />}
        </div>
        <div className="tc-popup-total" style={{ color: textColor }}>
          <span>{total !== null ? fmt(total) : '~'} / {contextLength >= 1024 ? `${Math.floor(contextLength / 1024)}k` : contextLength}</span>
          <span className="tc-popup-reserved">~{fmt(responseTokens)} reserved</span>
        </div>
        {!isGenerating && pct >= 20 && chatExecutionService.activeChatId && !chatExecutionService.activeSession.isCompacting && (
          <a className="tc-compact-btn" onClick={this._onCompactClick}>
            <ImgCompact size={12} />
            Compact context
          </a>
        )}
      </div>
    );
  }

  private _onCompactClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    const chatId = chatExecutionService.activeChatId;
    if (chatId) {
      void chatExecutionService.compactContext(chatId);
      this._closePopup();
    }
  };

  @action private _onMouseEnter = () => { this._isHovered = true; };
  @action private _onMouseLeave = () => { this._isHovered = false; };

  @action.bound
  private _closePopup() {
    if (!this._isOpen || this._isHidingPopup) return;
    this._isOpen = false;
    this._isHidingPopup = true;
    this._popupHideTimeout = setTimeout(action(() => {
      this._isHidingPopup = false;
      this._popupHideTimeout = null;
    }), 200);
  }

  @action
  private _onClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (this._isOpen) {
      this._closePopup();
    } else {
      if (this._popupHideTimeout !== null) {
        clearTimeout(this._popupHideTimeout);
        this._popupHideTimeout = null;
        this._isHidingPopup = false;
      }
      this._isOpen = true;
    }
  };

  @action
  private _onDocumentClick = () => {
    if (this._isOpen) this._closePopup();
  };

  private _doUpdateConversation = async () => {
    const { conversation, tools, hasImage, currentModel } = this.props;
    const cacheKey = this._makeConvKey();
    this._convCacheKey = cacheKey;

    const capabilities = currentModel?.capabilities ?? [];
    const hasVisionCapability = capabilities.includes('vision');
    const hasToolsCapability = capabilities.includes('tools');

    // Build compacted conversation: when a 'context' message is encountered,
    // replace the summarized messages with the summary text (same logic as server's _buildOllamaMessages).
    type ConvMsgEntry = { _id?: string; role: string; content: string; thinking?: string; toolName?: string; images?: { width: number; height: number }[] };
    const accumulated: ConvMsgEntry[] = [];
    for (const m of (conversation ?? [])) {
      if (m.role === 'root') continue;
      if (m.role === 'context') {
        const previousIdSet = new Set(m.previousMessageIds ?? []);
        const systemMsg = accumulated.find(r => r.role === 'system');
        const firstKeptIdx = m.previousMessageIds?.length
          ? accumulated.findIndex(r => r._id !== undefined && previousIdSet.has(r._id))
          : accumulated.length;
        const keptMessages = firstKeptIdx >= 0 ? accumulated.slice(firstKeptIdx) : [];
        accumulated.length = 0;
        if (systemMsg) accumulated.push(systemMsg);
        accumulated.push({ role: 'user', content: `[Conversation Summary]\n\n${m.content ?? ''}` });
        accumulated.push({ role: 'assistant', content: 'Understood. I will continue based on the conversation summary above.' });
        accumulated.push(...keptMessages);
        continue;
      }
      accumulated.push({
        _id: m.id,
        role: m.role,
        content: m.content ?? '',
        thinking: m.thinking,
        toolName: m.toolName,
        images: hasVisionCapability
          ? m.attachmentMetas
              ?.filter(a => a.isImage)
              .map(a => ({ width: a.imageWidth ?? 1024, height: a.imageHeight ?? 1024 }))
          : undefined,
      });
    }
    // Strip internal tracking IDs before sending to API
    const convMsgs = accumulated.map(({ _id: _unused, ...rest }) => rest);

    const result = await apiService.post<{ estimate: number; breakdown: TokensBreakdown }>('/api/tokens-estimate', {
      content: '',
      model: currentModel?.name,
      tools: hasToolsCapability ? tools : undefined,
      // hasImage covers pasted/camera images not yet in conversation history
      imageWidth: hasImage && hasVisionCapability ? 1024 : undefined,
      imageHeight: hasImage && hasVisionCapability ? 1024 : undefined,
      conversation: convMsgs,
      includeBreakdown: true,
    }, { loader: this._loader });

    runInAction(() => {
      this._convBreakdown = result.breakdown ?? null;
      this._calibrationFactor = result.breakdown ? this._getCalibrationFactor(result.breakdown) : 1;
      this.props.onTokensChange?.(this._total);
    });

  };

  private _doUpdatePrompt = async () => {
    const { inputValue, attachmentFiles, currentModel } = this.props;
    const hasVisionCapability = (currentModel?.capabilities ?? []).includes('vision');
    let content = inputValue.trim();

    // Estimate image tokens for image attachment files (14px patch size)
    let imageFileTokens = 0;
    if (hasVisionCapability && attachmentFiles && attachmentFiles.length > 0) {
      const imageFiles = attachmentFiles.filter(f => f.type.startsWith('image/'));
      for (const imgFile of imageFiles) {
        try {
          const bmp = await createImageBitmap(imgFile);
          const w = bmp.width, h = bmp.height;
          bmp.close();
          imageFileTokens += Math.ceil(w / 14) * Math.ceil(h / 14);
        } catch {
          imageFileTokens += Math.ceil(1024 / 14) * Math.ceil(1024 / 14); // fallback
        }
      }
    }

    // Read text attachment files and include in estimation
    if (attachmentFiles && attachmentFiles.length > 0) {
      const textChunks: string[] = [];
      for (const file of attachmentFiles) {
        const isText = file.type.startsWith('text/') ||
          /\.(txt|md|markdown|csv|json|xml|html|htm|yaml|yml|log|sql|css|js|ts|jsx|tsx|py|rb|go|rs|java|c|cpp|h|sh|bat|toml|ini|cfg|conf)$/i.test(file.name);
        if (isText) {
          try { textChunks.push(await file.text()); } catch { /* skip binary */ }
        }
      }
      if (textChunks.length > 0) content = (content ? content + '\n\n' : '') + textChunks.join('\n\n');
    }

    if (!content && imageFileTokens === 0) {
      runInAction(() => {
        this._promptTokens = 0;
        this.props.onTokensChange?.(this._total);
      });
      return;
    }

    const textEstimate = content
      ? (await apiService.post<{ estimate: number }>('/api/tokens-estimate', { content, model: currentModel?.name })).estimate
      : 0;
    runInAction(() => {
      this._promptTokens = textEstimate + imageFileTokens;
      this.props.onTokensChange?.(this._total);
    });

  };
}

export default TokensCounter;